import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { feeManagementAPI } from '../services/api';

const FeeDashboard = () => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Chart and Year Selector states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyIncome, setMonthlyIncome] = useState([]);
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchMonthlyIncome(selectedYear);
  }, [selectedYear]);

  const fetchMonthlyIncome = async (year) => {
    try {
      setLoadingChart(true);
      const response = await feeManagementAPI.getIncomeByMonth(year);
      setMonthlyIncome(response.data.monthlyIncome || []);
      if (response.data.years && response.data.years.length > 0) {
        setAvailableYears(response.data.years);
      }
    } catch (error) {
      console.error('Error fetching monthly income:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await feeManagementAPI.getStatistics(params);
      setStatistics(response.data.statistics);
      setRecentPayments(response.data.recentPayments);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      alert('Failed to fetch fee statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByDate = () => {
    fetchStatistics();
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(() => {
      fetchStatistics();
    }, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatShortCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'cash': '💵',
      'card': '💳',
      'upi': '📱',
      'bank-transfer': '🏦',
      'cheque': '📄',
      'other': '💰'
    };
    return icons[method] || '💰';
  };

  const getCollectionRate = () => {
    if (!statistics || statistics.totalFeeAmount === 0) return 0;
    return ((statistics.totalPaidAmount / statistics.totalFeeAmount) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        fontSize: '18px',
        color: '#718096'
      }}>
        Loading dashboard...
      </div>
    );
  }

  if (!statistics) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        fontSize: '18px',
        color: '#718096'
      }}>
        Failed to load statistics
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '20px auto', background: '#ffffff', minHeight: 'calc(100vh - 100px)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', color: '#1a202c' }}>
            📊 Fee Dashboard
          </h1>
          <p style={{ margin: 0, color: '#718096' }}>
            Overview of fee collection and payments
          </p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            onClick={() => navigate('/admin/fee-management')}
            style={{
              padding: '12px 24px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Manage Fees
          </button>
          <button
            onClick={() => navigate('/admin/pending-fees')}
            style={{
              padding: '12px 24px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            View Reminders
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div style={{
        background: '#f7fafc',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontWeight: '500', color: '#4a5568', fontSize: '14px' }}>
            📅 Filter by Payment Date:
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate || new Date().toISOString().split('T')[0]}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13px',
              outline: 'none'
            }}
          />
          <span style={{ color: '#a0aec0' }}>—</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={new Date().toISOString().split('T')[0]}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleFilterByDate}
            disabled={!startDate && !endDate}
            style={{
              padding: '8px 20px',
              background: (!startDate && !endDate) ? '#cbd5e0' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (!startDate && !endDate) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Apply Filter
          </button>
          {(startDate || endDate) && (
            <button
              onClick={handleClearDateFilter}
              style={{
                padding: '8px 20px',
                background: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Total Fee Amount */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            Total Fee Amount
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
            {formatCurrency(statistics.totalFeeAmount)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            {statistics.totalUsers} students enrolled
          </div>
        </div>

        {/* Collected Amount */}
        <div style={{
          background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            Collected Amount
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
            {formatCurrency(statistics.totalPaidAmount)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            {getCollectionRate()}% collection rate
          </div>
        </div>

        {/* Pending Amount */}
        <div style={{
          background: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            Pending Amount
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
            {formatCurrency(statistics.totalPendingAmount)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            {statistics.overdueCount} overdue payments
          </div>
        </div>

        {/* Average Fee */}
        <div style={{
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          padding: '25px',
          borderRadius: '12px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
            Average Fee per Student
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
            {formatCurrency(statistics.totalUsers > 0 ? statistics.totalFeeAmount / statistics.totalUsers : 0)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>
            Across all enrolled students
          </div>
        </div>
      </div>

      {/* Monthly Income Breakdown Chart */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '30px',
        position: 'relative'
      }}>
        {/* Chart Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '6px', fontSize: '18px', color: '#1a202c', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📊 Monthly Income Breakdown
            </h3>
            <p style={{ margin: 0, color: '#718096', fontSize: '13px' }}>
              Monthly fee collection trends for {selectedYear}
            </p>
          </div>

          {/* Year selector dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Select Year:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                padding: '8px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#4a5568',
                backgroundColor: '#ffffff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderColor: '#667eea'
              }}
            >
              {availableYears.map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Dynamic Tooltip Overlay */}
        {hoveredBar !== null && monthlyIncome[hoveredBar] && (
          <div style={{
            position: 'absolute',
            top: '75px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(26, 32, 44, 0.95)',
            color: 'white',
            padding: '10px 18px',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: '600',
            pointerEvents: 'none',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.15s ease'
          }}>
            <span style={{ fontSize: '11px', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '4px' }}>
              {monthlyIncome[hoveredBar].monthName} {selectedYear}
            </span>
            <span style={{ fontSize: '16px', color: '#4ade80', fontWeight: '700' }}>
              {formatCurrency(monthlyIncome[hoveredBar].totalIncome)}
            </span>
          </div>
        )}

        {loadingChart ? (
          <div style={{
            height: '280px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#718096',
            fontSize: '14px'
          }}>
            Updating chart metrics...
          </div>
        ) : monthlyIncome.length === 0 || monthlyIncome.every(m => m.totalIncome === 0) ? (
          <div style={{
            height: '280px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#718096',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px dashed #e2e8f0',
            boxSizing: 'border-box'
          }}>
            <span style={{ fontSize: '32px', marginBottom: '10px' }}>💸</span>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>No income recorded in {selectedYear}</span>
            <span style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>Select another year to view statistics</span>
          </div>
        ) : (
          <div style={{ position: 'relative', overflowX: 'auto' }}>
            <svg
              viewBox="0 0 800 300"
              width="100%"
              style={{
                minWidth: '600px',
                height: 'auto',
                display: 'block'
              }}
            >
              <defs>
                <linearGradient id="barGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#48bb78" />
                  <stop offset="100%" stopColor="#38a169" />
                </linearGradient>
              </defs>

              {/* Grid Ticks Calculations */}
              {(() => {
                const maxIncome = Math.max(...monthlyIncome.map(m => m.totalIncome));
                const graphMax = maxIncome > 0 ? maxIncome : 10000;
                
                const margin = { left: 80, right: 20, top: 20, bottom: 40 };
                const graphWidth = 800 - margin.left - margin.right;
                const graphHeight = 300 - margin.top - margin.bottom;

                // 4 grid divisions
                const gridTicks = [0, 0.25, 0.5, 0.75, 1];

                return (
                  <>
                    {/* Horizontal Gridlines & Y-Axis Labels */}
                    {gridTicks.map((tickRatio, idx) => {
                      const value = graphMax * tickRatio;
                      const yPos = margin.top + graphHeight - (tickRatio * graphHeight);
                      return (
                        <g key={idx}>
                          <line
                            x1={margin.left}
                            y1={yPos}
                            x2={800 - margin.right}
                            y2={yPos}
                            stroke="#e2e8f0"
                            strokeWidth="1"
                            strokeDasharray={idx === 0 ? "none" : "5,5"}
                          />
                          <text
                            x={margin.left - 12}
                            y={yPos + 4}
                            textAnchor="end"
                            fill="#718096"
                            fontSize="11"
                            fontWeight="500"
                            fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
                          >
                            {formatShortCurrency(value)}
                          </text>
                        </g>
                      );
                    })}

                    {/* Columns & X-Axis Labels */}
                    {monthlyIncome.map((data, i) => {
                      const colWidth = (graphWidth / 12) * 0.55;
                      const spacing = (graphWidth / 12) * 0.45;
                      const xPos = margin.left + i * (colWidth + spacing) + (spacing / 2);
                      
                      const barHeight = (data.totalIncome / graphMax) * graphHeight;
                      const yPos = margin.top + graphHeight - barHeight;

                      const isHovered = hoveredBar === i;

                      return (
                        <g key={i}>
                          {/* Column Bar */}
                          <rect
                            x={xPos}
                            y={yPos}
                            width={colWidth}
                            height={Math.max(barHeight, 0)}
                            rx="5"
                            ry="5"
                            fill="url(#barGrad)"
                            style={{
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: 'pointer',
                              filter: isHovered ? 'drop-shadow(0 4px 10px rgba(56, 161, 105, 0.4))' : 'none',
                              opacity: hoveredBar !== null && !isHovered ? 0.75 : 1
                            }}
                            onMouseEnter={() => setHoveredBar(i)}
                            onMouseLeave={() => setHoveredBar(null)}
                          />

                          {/* Base tiny line if income is 0 */}
                          {data.totalIncome === 0 && (
                            <line
                              x1={xPos}
                              y1={margin.top + graphHeight}
                              x2={xPos + colWidth}
                              y2={margin.top + graphHeight}
                              stroke="#cbd5e0"
                              strokeWidth="2"
                            />
                          )}

                          {/* Invisible overlay for large hover touchpoints */}
                          <rect
                            x={xPos - spacing / 2}
                            y={margin.top}
                            width={colWidth + spacing}
                            height={graphHeight}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredBar(i)}
                            onMouseLeave={() => setHoveredBar(null)}
                          />

                          {/* X-Axis Month label */}
                          <text
                            x={xPos + colWidth / 2}
                            y={300 - 15}
                            textAnchor="middle"
                            fill={isHovered ? '#1a202c' : '#718096'}
                            fontSize="12"
                            fontWeight={isHovered ? '700' : '500'}
                            fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
                            style={{ transition: 'all 0.2s' }}
                          >
                            {data.monthName}
                          </text>
                        </g>
                      );
                    })}
                  </>
                );
              })()}
            </svg>
          </div>
        )}
      </div>

      {/* Status Breakdown */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>
          Payment Status Breakdown
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          {/* Fully Paid */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#48bb78',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 auto 15px'
            }}>
              {statistics.paidCount}
            </div>
            <div style={{ fontWeight: '500', color: '#1a202c', marginBottom: '5px' }}>
              Fully Paid
            </div>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              {statistics.totalUsers > 0 ? ((statistics.paidCount / statistics.totalUsers) * 100).toFixed(0) : 0}% of students
            </div>
          </div>

          {/* Partial Payment */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#fbbf24',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 auto 15px'
            }}>
              {statistics.partialCount}
            </div>
            <div style={{ fontWeight: '500', color: '#1a202c', marginBottom: '5px' }}>
              Partial Payment
            </div>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              {statistics.totalUsers > 0 ? ((statistics.partialCount / statistics.totalUsers) * 100).toFixed(0) : 0}% of students
            </div>
          </div>

          {/* Not Started */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#a0aec0',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 auto 15px'
            }}>
              {statistics.notStartedCount}
            </div>
            <div style={{ fontWeight: '500', color: '#1a202c', marginBottom: '5px' }}>
              Not Started
            </div>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              {statistics.totalUsers > 0 ? ((statistics.notStartedCount / statistics.totalUsers) * 100).toFixed(0) : 0}% of students
            </div>
          </div>

          {/* Overdue */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#f56565',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 auto 15px'
            }}>
              {statistics.overdueCount}
            </div>
            <div style={{ fontWeight: '500', color: '#1a202c', marginBottom: '5px' }}>
              Overdue
            </div>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              {statistics.totalUsers > 0 ? ((statistics.overdueCount / statistics.totalUsers) * 100).toFixed(0) : 0}% of students
            </div>
          </div>
        </div>
      </div>

      {/* Collection Progress Bar */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '18px' }}>
          Overall Fee Collection Progress
        </h3>
        
        <div style={{ marginBottom: '15px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px',
            fontSize: '14px',
            color: '#718096'
          }}>
            <span>Collected: {formatCurrency(statistics.totalPaidAmount)}</span>
            <span>Target: {formatCurrency(statistics.totalFeeAmount)}</span>
          </div>
          
          <div style={{
            width: '100%',
            height: '30px',
            background: '#e2e8f0',
            borderRadius: '15px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${getCollectionRate()}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
              transition: 'width 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              {getCollectionRate()}%
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '13px',
          color: '#718096',
          marginTop: '10px'
        }}>
          <span>Remaining: {formatCurrency(statistics.totalPendingAmount)}</span>
          <span>{(100 - parseFloat(getCollectionRate())).toFixed(1)}% pending</span>
        </div>
      </div>

      {/* Recent Payments */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>
          Recent Payments
        </h3>

        {recentPayments.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#718096',
            fontSize: '14px'
          }}>
            No payments recorded yet
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Receipt #
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Student
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Amount
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Method
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
                    Recorded By
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.map((payment) => (
                  <tr 
                    key={payment._id}
                    style={{ 
                      borderBottom: '1px solid #e2e8f0',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                      }}>
                        {payment.receiptNumber}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <div style={{ fontWeight: '500', color: '#1a202c' }}>
                        {payment.user?.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {payment.user?.email}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#48bb78' }}>
                      {formatCurrency(payment.amount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <span style={{ marginRight: '5px' }}>
                        {getPaymentMethodIcon(payment.paymentMethod)}
                      </span>
                      {payment.paymentMethod.replace('-', ' ').toUpperCase()}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                      {payment.recordedBy?.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeDashboard;
